/**
 * Item ref resolver — classifies each preset item's ref and produces an apply plan.
 *
 * Three ref types:
 *   - Asset ref:      `<publisher>/<name>@<version>` → create new Instance + assign
 *   - Library ref:    `library://<tier>/instances/<id>` → reuse existing Instance
 *   - Nested Preset:  another preset ref → recursively apply
 *   - Cross-item ref: `#<item-id>` → resolved at apply-time from sibling item results
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */

import type { PresetItem } from "@skaile/workspaces/types/manifests";
import type { AssetDefinition, IAssetIndex, Instance } from "../library.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Classification of a preset item's ref string.
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export type ItemRefKind = "asset" | "library-instance" | "nested-preset" | "cross-item";

/**
 * A resolved preset item whose ref is an asset definition ref.
 *
 * At apply-time, a new Instance will be created from `defRef`.
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export interface ResolvedAssetItem {
  kind: "asset";
  item: PresetItem;
  /** The asset definition ref (may include version). */
  defRef: string;
  /** Resolved asset definition from library cache (null if not cached). */
  assetDef: AssetDefinition | null;
}

/**
 * A resolved preset item whose ref is a `library://` URI pointing to an existing Instance.
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export interface ResolvedLibraryItem {
  kind: "library-instance";
  item: PresetItem;
  /** The instance UUID extracted from the library:// URI. */
  instanceId: string;
  /** The resolved instance (null if not found). */
  instance: Instance | null;
}

/**
 * A resolved preset item that recursively refers to another preset.
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export interface ResolvedNestedPresetItem {
  kind: "nested-preset";
  item: PresetItem;
  /** The nested preset ref. */
  presetRef: string;
}

/**
 * A resolved preset item that is a cross-item reference (`#<item-id>`).
 *
 * Resolved at apply-time from sibling item results (items must be ordered).
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export interface ResolvedCrossItemRef {
  kind: "cross-item";
  item: PresetItem;
  /** The target item id (without the # prefix). */
  targetId: string;
}

/**
 * Discriminated union of all resolved preset item types.
 *
 * @docLink packages/library/concepts#preset-item-resolution
 */
export type ResolvedItem =
  | ResolvedAssetItem
  | ResolvedLibraryItem
  | ResolvedNestedPresetItem
  | ResolvedCrossItemRef;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify a single item ref string into its {@link ItemRefKind}.
 *
 * Returns `"asset"` by default when the ref doesn't match a known prefix.
 * The caller (apply.ts) re-classifies based on `knownPresetRefs`.
 *
 * @param ref - The raw ref string from a preset item.
 * @returns The classified kind.
 * @docLink packages/library/concepts#preset-item-resolution
 */
export function classifyRef(ref: string): ItemRefKind {
  if (ref.startsWith("library://")) return "library-instance";
  if (ref.startsWith("#")) return "cross-item";
  // Distinguish nested presets from assets: nested presets are identified by
  // the caller providing a known-presets set. Without that context, we default
  // to "asset". The caller (apply.ts) reclassifies based on discovery.
  return "asset";
}

/**
 * Extract the instance UUID from a `library://` URI.
 *
 * Format: `library://<tier>/instances/<uuid>` where tier is `personal | team | org`.
 *
 * @param ref - The full `library://` URI string.
 * @returns The UUID string, or null if the URI is malformed.
 * @docLink packages/library/concepts#preset-item-resolution
 */
export function extractLibraryInstanceId(ref: string): string | null {
  const match = ref.match(/^library:\/\/[^/]+\/instances\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

/**
 * Resolve a single preset item against the library.
 *
 * @param item - The preset item to resolve.
 * @param library - The library to look up definitions/instances.
 * @param knownPresetRefs - Set of refs known to be presets (for nested detection).
 * @returns A typed {@link ResolvedItem} discriminated by kind.
 * @docLink packages/library/concepts#preset-item-resolution
 */
export async function resolveItem(
  item: PresetItem,
  library: IAssetIndex,
  knownPresetRefs?: Set<string>,
): Promise<ResolvedItem> {
  const ref = item.ref;

  // Cross-item ref
  if (ref.startsWith("#")) {
    return { kind: "cross-item", item, targetId: ref.slice(1) };
  }

  // Library instance ref
  if (ref.startsWith("library://")) {
    const instanceId = extractLibraryInstanceId(ref);
    if (!instanceId) {
      return { kind: "library-instance", item, instanceId: "", instance: null };
    }
    const instance = await library.getInstance(instanceId);
    return { kind: "library-instance", item, instanceId, instance };
  }

  // Nested preset (detected via knownPresetRefs)
  if (knownPresetRefs?.has(ref)) {
    return { kind: "nested-preset", item, presetRef: ref };
  }

  // Asset ref (default)
  const assetDef = await library.getAssetDef(ref);
  return { kind: "asset", item, defRef: ref, assetDef };
}

/**
 * Resolve all items in a preset in parallel.
 *
 * @param items - Array of preset items to resolve.
 * @param library - The library to look up definitions/instances.
 * @param knownPresetRefs - Set of refs known to be presets.
 * @returns Array of resolved items in the same order as the input.
 * @docLink packages/library/concepts#preset-item-resolution
 */
export async function resolveAllItems(
  items: PresetItem[],
  library: IAssetIndex,
  knownPresetRefs?: Set<string>,
): Promise<ResolvedItem[]> {
  return Promise.all(items.map((item) => resolveItem(item, library, knownPresetRefs)));
}
